import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Product } from 'src/app/models/woocommerce-product.model';
import { WooCommerceService } from 'src/app/services/woocommerce.service';
import { EventsService } from 'src/app/services/events.service';

@Component({
  selector: 'page-store',
  templateUrl: 'store.page.html',
  styleUrls: ['store.page.scss']
})
export class StorePage implements OnInit {
  public page$: Observable<any>;
  public products$: Observable<Product[]>;
  public loading = true;
  public error = false;
  /** Factor de conversión para mostrar precios en puntos (API). */
  conversionFactor = 1;
  /** Nombre de la unidad de puntos (API). */
  pointsName = '';

  constructor(
    private router: Router,
    private woocommerceService: WooCommerceService,
    private events: EventsService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.loadProducts();
  }

  ionViewWillEnter() {
    this.loadProducts();
  }

  loadProducts() {
    this.loading = true;
    this.error = false;
    const cached = this.woocommerceService.getCachedStoreUserInfo();
    if (cached) {
      this.conversionFactor = this.woocommerceService.getConversionFactorFromUserInfo(cached);
      this.pointsName = this.woocommerceService.getPointsNameFromUserInfo(cached);
    }
    this.page$ = this.woocommerceService.getStoreUserInfo().pipe(
      tap((res: any) => {
        this.conversionFactor = this.woocommerceService.getConversionFactorFromUserInfo(res);
        this.pointsName = this.woocommerceService.getPointsNameFromUserInfo(res);
      }),
      map(() => ({ title: this.translate.instant('STORE-TITLE') })),
      tap((page) => this.events.pages$$.next(page.title)),
      catchError(() => {
        this.error = true;
        this.loading = false;
        return of({ title: this.translate.instant('STORE-TITLE') });
      }),
      shareReplay()
    );
    this.products$ = this.page$.pipe(
      switchMap(() => this.woocommerceService.getProducts()),
      map((res: any) => res?.products ?? (Array.isArray(res) ? res : [])),
      tap(() => { this.loading = false; }),
      catchError(() => {
        this.error = true;
        this.loading = false;
        return of([]);
      }),
      shareReplay()
    );
  }

  /** Puntos a mostrar (precio × factor de conversión). */
  getDisplayPoints(product: Product): number | null {
    const base = product.price_in_points ?? (product.price != null ? parseFloat(String(product.price)) : null);
    if (base == null || isNaN(Number(base)) || Number(base) === 1) return null;
    return Math.round(Number(base) * this.conversionFactor);
  }

  /** True si el precio del producto es 1 y no debe mostrarse. */
  isPriceHidden(product: Product): boolean {
    const base = product.price_in_points ?? (product.price != null ? parseFloat(String(product.price)) : null);
    return Number(base) === 1;
  }

  goToProductDetail(product: Product) {
    this.router.navigate(['store', 'product', product.id]);
  }

  goToOrders() {
    this.router.navigate(['store', 'orders']);
  }

  refresh(event?: any) {
    this.loadProducts();
    if (event?.target?.complete) {
      event.target.complete();
    }
  }
}
